import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import { MiscellaneousItem, Truckload } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { bundlesLoaded } from '../store/bundlesSlice';
import { truckloadCreated, truckloadDeleted, truckloadsLoaded } from '../store/truckloadsSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export function loadTruckloads(spinner = true): AsyncThunkActionVoid {
  return async function loadTruckloadsThunkAction(dispatch) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading truckloads...')) : null;
    try {
      const truckloads = await designService.truckloads.getActive();
      dispatch(truckloadsLoaded(truckloads));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading truckloads'));
    } finally {
      if (spinnerId) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function loadTruckloadsForProject(projectGuid: string, spinner = true): AsyncThunkActionVoid {
  return async function loadTruckloadsForProjectThunkAction(dispatch) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading truckloads...')) : null;
    try {
      const truckloads = await designService.truckloads.getForProject(projectGuid);
      dispatch(truckloadsLoaded(truckloads));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading truckloads'));
    } finally {
      if (spinnerId) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createTruckload(
  bundleGuids: readonly string[],
  trailerGuid: string | null,
  truckDriverGuid: string | null,
  dateShipped: Date | null,
  miscellaneousItems: readonly MiscellaneousItem[],
): AsyncThunkActionVoid {
  return async function createTruckloadThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Creating truckload...'));

    try {
      const state = getState();
      const bundles = bundleGuids.map((bundleGuid) => state.bundles[bundleGuid]);
      const bundlesAlreadyAssignedToTruckload = bundles.filter((bundle) => bundle.truckloadGuid !== null);
      if (bundlesAlreadyAssignedToTruckload.length > 0) throw new Error('Bundles are already assigned to a truckload');

      const truckload = await designService.truckloads.insert(
        trailerGuid,
        truckDriverGuid,
        dateShipped,
        miscellaneousItems,
      );
      await designService.bundles.assignToTruckload(bundleGuids, truckload.guid);
      dispatch(truckloadCreated({ truckload, bundleGuids }));
    } catch {
      dispatch(enqueueError('Error creating truckload'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function editTruckload(
  guid: string,
  trailerGuid: string | null,
  truckDriverGuid: string | null,
  shipDate: Date | null,
  miscellaneousItems: readonly MiscellaneousItem[],
): AsyncThunkActionVoid {
  return async function editTruckloadThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Editing truckload...'));

    try {
      const existingTruckload = getState().truckloads[guid];
      const updatedTruckload: Truckload = {
        ...existingTruckload,
        trailerGuid,
        truckDriverGuid,
        dateShipped: shipDate?.toISOString() ?? null,
        miscellaneousItems,
      };

      await designService.truckloads.update(updatedTruckload);
      dispatch(truckloadsLoaded([updatedTruckload]));
    } catch {
      dispatch(enqueueError('Error editing truckload'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function addBundlesToTruckload(truckloadGuid: string, bundleGuids: readonly string[]): AsyncThunkActionVoid {
  return async function addBundlesToTruckloadThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Adding bundles to truckload...'));

    try {
      const updatedBundles = await designService.bundles.assignToTruckload(bundleGuids, truckloadGuid);
      dispatch(bundlesLoaded(updatedBundles));
    } catch (exception) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error adding bundles to truckload')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeBundleFromTruckload(bundleGuid: string): AsyncThunkActionVoid {
  return async function removeBundleFromTruckloadThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Removing bundle from truckload...'));

    try {
      const updatedBundle = await designService.bundles.removeFromTruckload(bundleGuid);
      dispatch(bundlesLoaded([updatedBundle]));
    } catch (exception) {
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error removing bundle from truckload')),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function deleteTruckload(truckloadGuid: string): AsyncThunkActionVoid {
  return async function deleteTruckloadThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Deleting truckload...'));

    try {
      const updatedBundles = await designService.truckloads.remove(truckloadGuid);
      dispatch(truckloadDeleted({ truckloadGuid, updatedBundles }));
    } catch (exception) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error deleting truckload')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
